/**
 * Effect-only hook that drives the data-accumulator's `fetchAll` / `abortAll`
 * lifecycle for the single-order page, and primes the Firebase form config
 * (no live listener) on each `(orderId, orgId)` change when it isn't already
 * cached in Redux.
 *
 * On unmount or order/org change, also clears staged denial/query reasons for
 * the departing order from UserStore.
 *
 * Extracted from {@link SingleOrderLayout} so the container stays
 * declarative and the effect can be unit-tested in isolation.
 *
 * @module useSingleOrderDataLifecycle
 */
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearDenialQueryReasonsFromStoreByOrderId } from "../../../components/modals/StatusUpdateModal/utils/denialQueryReasonUserStoreHelper";
import { fetchFormConfigfromFirebaseWithoutLiveListen } from "../../../redux/slice/formConfigNoLiveListenSlice";
import { getMedicalPaOrderDetailsFor } from "../../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../../redux/store/store";

/**
 * Arguments for {@link useSingleOrderDataLifecycle}.
 */
interface UseSingleOrderDataLifecycleArgs {
  /** PA order ID whose data should be loaded; effect is inert when undefined. */
  orderId: string | undefined;
  /** Organisation ID scoping every domain query; effect is inert when undefined. */
  orgId: string | undefined;
  /**
   * Accumulator fetch trigger. Pass `{ force: false }` to reuse the provider's
   * per-order cache; omit for a forced refetch.
   */
  fetchAll: (
    orderId: string,
    orgId: string,
    options?: { force?: boolean },
  ) => Promise<void>;
  /** Aborts every in-flight accumulator domain request. */
  abortAll: () => void;
}

/**
 * Triggers `fetchAll` whenever both `orderId` and `orgId` are available,
 * dispatches the form-config fetch only when it isn't already in the store,
 * and on unmount or input change aborts in-flight domain requests, clears
 * staged denial/query reasons for the order, and unsubscribes the medical PA
 * order listener.
 *
 * @param args - Order/org identifiers and the `fetchAll` / `abortAll`
 *   callbacks supplied by the data accumulator
 */
export const useSingleOrderDataLifecycle = ({
  orderId,
  orgId,
  fetchAll,
  abortAll,
}: UseSingleOrderDataLifecycleArgs): void => {
  const dispatch = useDispatch<AppDispatch>();
  const { unsubscribe } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const unsubscribeRef = useRef(unsubscribe);
  unsubscribeRef.current = unsubscribe;

  // The form config is a global (non-order-scoped) collection that persists in
  // Redux across navigation. Re-fetching it on every mount flips the shared
  // loading flag and flashes the full-page loader even when the order data is
  // already cached, so only fetch it the first time it's missing.
  const { formConfigs } = useSelector(
    (state: RootState) => state.formConfigNoLiveListen,
  );
  const hasFormConfigsRef = useRef(formConfigs.length > 0);
  hasFormConfigsRef.current = formConfigs.length > 0;

  useEffect(() => {
    if (orderId && orgId) {
      // Reuse the accumulator's cache when this order was already loaded by a
      // sibling screen; only fetch when the data isn't present yet.
      fetchAll(orderId, orgId, { force: false });
      if (!hasFormConfigsRef.current) {
        dispatch(fetchFormConfigfromFirebaseWithoutLiveListen());
      }
      dispatch(getMedicalPaOrderDetailsFor(orderId));
    }
    return () => {
      abortAll();
      if (orderId) {
        clearDenialQueryReasonsFromStoreByOrderId(orderId);
      }
      unsubscribeRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orgId]);
};
